/**
 * Personal data (CONTEXT.md): Bookmarks and Recents. Never on a Character and never in an
 * export (issue #15); one document each on the Account once the Device signs in (#25, ADR-0017
 * supersedes #15's device-only ruling).
 *
 * The `apply*` writers at the end are the sync engine's: they write what another Device sent
 * without announcing it as a local change and without trimming, since the engine trims once
 * after a whole pull.
 */
import { recordChanged } from "./records";
import { STORE_BOOKMARKS, STORE_RECENTS, complete, request, transaction } from "./store";

/** How many Recents are kept; older ones fall off when a new one is recorded. */
export const RECENTS_CAP = 50;

export interface Bookmark {
  uuid: string;
  addedAt: string;
}

export interface Recent {
  uuid: string;
  viewedAt: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Called after any change to Bookmarks or Recents, so open screens can refresh. */
export function subscribePersonal(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function changed(): void {
  for (const l of listeners) l();
}

/** Every Bookmark, most recently added first. */
export async function listBookmarks(): Promise<Bookmark[]> {
  const tx = await transaction([STORE_BOOKMARKS], "readonly");
  const rows = (await request(tx.objectStore(STORE_BOOKMARKS).index("addedAt").getAll())) as Bookmark[];
  return rows.reverse();
}

export async function getBookmark(uuid: string): Promise<Bookmark | undefined> {
  const tx = await transaction([STORE_BOOKMARKS], "readonly");
  return (await request(tx.objectStore(STORE_BOOKMARKS).get(uuid))) as Bookmark | undefined;
}

export async function isBookmarked(uuid: string): Promise<boolean> {
  const tx = await transaction([STORE_BOOKMARKS], "readonly");
  return (await request(tx.objectStore(STORE_BOOKMARKS).getKey(uuid))) !== undefined;
}

/** Mark or unmark an Entry. Marking again keeps the original time. */
export async function setBookmark(uuid: string, on: boolean, at = new Date().toISOString()): Promise<void> {
  const tx = await transaction([STORE_BOOKMARKS], "readwrite");
  const store = tx.objectStore(STORE_BOOKMARKS);
  const existing = (await request(store.get(uuid))) as Bookmark | undefined;
  if (on) {
    if (!existing) store.put({ uuid, addedAt: at } satisfies Bookmark);
  } else {
    store.delete(uuid);
  }
  await complete(tx);
  recordChanged("bookmark", uuid, on ? "saved" : "deleted", on ? undefined : existing);
  changed();
}

export async function getRecent(uuid: string): Promise<Recent | undefined> {
  const tx = await transaction([STORE_RECENTS], "readonly");
  return (await request(tx.objectStore(STORE_RECENTS).get(uuid))) as Recent | undefined;
}

/** Every Recent, newest first. */
export async function listRecents(): Promise<Recent[]> {
  const tx = await transaction([STORE_RECENTS], "readonly");
  const rows = (await request(tx.objectStore(STORE_RECENTS).index("viewedAt").getAll())) as Recent[];
  return rows.reverse();
}

/** Record a viewing: the Entry moves to the front (one row per Entry) and the list is trimmed to the cap. */
export async function recordRecent(uuid: string, at = new Date().toISOString()): Promise<void> {
  const tx = await transaction([STORE_RECENTS], "readwrite");
  const store = tx.objectStore(STORE_RECENTS);
  store.put({ uuid, viewedAt: at } satisfies Recent);
  const keys = (await request(store.index("viewedAt").getAllKeys())) as string[];
  for (const key of keys.slice(0, Math.max(0, keys.length - RECENTS_CAP))) store.delete(key);
  await complete(tx);
  recordChanged("recent", uuid, "saved");
  changed();
}

/**
 * Forget every Recent on this Device. A device-only act: the Account keeps its own Recents, so
 * clearing here never empties the list on another Device (ADR-0017 merges Recents by time).
 */
export async function clearRecents(): Promise<void> {
  const tx = await transaction([STORE_RECENTS], "readwrite");
  tx.objectStore(STORE_RECENTS).clear();
  await complete(tx);
  changed();
}

/** Write a Bookmark as another Device holds it (#25). Announces nothing: the engine knows. */
export async function applyBookmark(bookmark: Bookmark): Promise<void> {
  const tx = await transaction([STORE_BOOKMARKS], "readwrite");
  tx.objectStore(STORE_BOOKMARKS).put(bookmark);
  await complete(tx);
  changed();
}

/** Remove a Bookmark another Device removed (#25). */
export async function applyBookmarkRemoval(uuid: string): Promise<void> {
  const tx = await transaction([STORE_BOOKMARKS], "readwrite");
  tx.objectStore(STORE_BOOKMARKS).delete(uuid);
  await complete(tx);
  changed();
}

/** Write a Recent as another Device holds it (#25), cap and all left to `trimRecents`. */
export async function applyRecent(recent: Recent): Promise<void> {
  const tx = await transaction([STORE_RECENTS], "readwrite");
  tx.objectStore(STORE_RECENTS).put(recent);
  await complete(tx);
  changed();
}

export async function applyRecentRemoval(uuid: string): Promise<void> {
  const tx = await transaction([STORE_RECENTS], "readwrite");
  tx.objectStore(STORE_RECENTS).delete(uuid);
  await complete(tx);
  changed();
}

